import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FORM_STATES, Form, useFormService } from '@bingads-webui-react/form';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withSiteMap, processorSet, processor } from '@bingads-webui-react/with-site-map';
import { useBackToOverview } from './use-back-to-overview';
import { useSettingsFormAction } from './use-settings-form-actions';
import { SDKSettingsActionBar } from './action-bar';

const getStyles = () => ({
  sdkSettingsFormWrapper: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    flexDirection: 'column',
    margin: '24px 43px 27px 43px',
    boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.12), 0px 2px 4px 0px rgba(0, 0, 0, 0.14)',
    border: '1px solid transparent',
    '& .react-block-ui, & .react-block-ui-none': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
  },
  sdkSettingsForm: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    borderRadius: 4,
    background: '#f9f9f9',
    padding: '24px 32px 24px 32px',
  },
});

const SDKSettingsViewInternal = React.memo((props) => {
  const {
    ContentComponent, siteMap,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const { backToOverviewPage } = useBackToOverview({ siteMap });
  const { onClickSaveButton } = useSettingsFormAction(props);
  const { state } = useFormService();

  return (
    <div className={classes.sdkSettingsFormWrapper}>
      <UiBlocker blocking={state.matches(FORM_STATES.SAVING)} loadingText={i18n.getString(_TL_('Saving...'))}>
        <div className={classes.sdkSettingsForm}>
          <ContentComponent {...props} />
        </div>
        <SDKSettingsActionBar onCancel={backToOverviewPage} onSubmit={onClickSaveButton} />
      </UiBlocker>
    </div>
  );
});
SDKSettingsViewInternal.propTypes = {
  siteMap: PropTypes.shape({}).isRequired,
  ContentComponent: PropTypes.func.isRequired,
  repository: PropTypes.func.isRequired,
};

const SDKSettingsView = withSiteMap(SDKSettingsViewInternal, [processor.processPath, ...processorSet.basic]);

const SDKSettingsFormInternal = ({
  id, ContentComponent, repository, viewProps, siteMap,
}) => {
  const FormViewWrapper = useCallback(({
    data,
    onDataChange,
  }) => (
    <SDKSettingsView
      {...viewProps}
      ContentComponent={ContentComponent}
      repository={repository}
      data={data}
      onDataChange={onDataChange}
    />
  ), [ContentComponent, repository, viewProps]);

  const { backToOverviewPage } = useBackToOverview({ siteMap });
  const config = useMemo(() => ({
    id,
    view: FormViewWrapper,
    repository,
    machineOptions: {
      initialState: FORM_STATES.FETCHING,
      actions: {
        onDoneSaving: backToOverviewPage,
      },
    },
  }), [FormViewWrapper, backToOverviewPage, id, repository]);

  return (
    <Form config={config} />
  );
};

SDKSettingsFormInternal.propTypes = {
  siteMap: PropTypes.shape({}).isRequired,
  id: PropTypes.string.isRequired,
  ContentComponent: PropTypes.func.isRequired,
  repository: PropTypes.func.isRequired,
  viewProps: PropTypes.shape({}),
};

SDKSettingsFormInternal.defaultProps = {
  viewProps: {},
};

export const SDKSettingsForm = withSiteMap(SDKSettingsFormInternal, [processor.processPath, ...processorSet.basic]);
